import { type IconDefinition, faCircle } from '@fortawesome/free-regular-svg-icons';
import {
  faCheckCircle,
  faCircleExclamation,
  faCircleXmark,
  faFileCircleExclamation,
  faFilePen,
  faListCheck,
  faPeopleArrows,
  faPersonWalkingArrowLoopLeft,
  faRotate,
  faUser,
  faUserEdit
} from '@fortawesome/free-solid-svg-icons';
import { systemError } from 'feature-components/system-error-alert';
import { camelcaseToTitlecase } from 'helpers/string.helpers';
import {
  MarkStatus,
  MarkType
} from 'services/assist-api/entities/test-instance-response-mark/types';

/**
 * Get fontawesome icons for the mark status.
 */
export const getMarkStatusIcon = (status: MarkStatus): IconDefinition => {
  switch (status) {
    case MarkStatus.Assigned:
      return faUser;
    case MarkStatus.InProgress:
      return faUserEdit;
    case MarkStatus.NeedsReview:
      return faCircleExclamation;
    case MarkStatus.InReview:
      return faListCheck;
    case MarkStatus.FurtherEscalated:
      return faFileCircleExclamation;
    case MarkStatus.ReturnedForMarking:
      return faPeopleArrows;
    case MarkStatus.ResponseReopened:
      return faFilePen;
    case MarkStatus.Reassigned:
      return faPersonWalkingArrowLoopLeft;
    case MarkStatus.FinishedSuccess:
      return faCheckCircle;
    case MarkStatus.FinishedFailure:
      return faCircleXmark;
    case MarkStatus.Replaced:
      return faRotate;
    default:
      return faCircle;
  }
};

export function friendlyMarkStatus(status: MarkStatus | null): string {
  // If a status is null, we can consider it to be assigned (not created/started yet), since marks
  // are only created when they are first saved.
  if (status === null) {
    return camelcaseToTitlecase(MarkStatus.Assigned);
  }
  return camelcaseToTitlecase(status.replace('finished', 'finished:')).replace('For', 'for');
}

export const reviewMarkStatuses = [
  MarkStatus.NeedsReview,
  MarkStatus.InReview,
  MarkStatus.FurtherEscalated
];

export const nonReviewDraftMarkStatuses = [
  MarkStatus.InProgress,
  MarkStatus.ReturnedForMarking,
  MarkStatus.Reassigned
];

export const draftMarkStatuses = reviewMarkStatuses.concat(nonReviewDraftMarkStatuses);

export function nonReviewMarkStatuses(): MarkStatus[] {
  return Object.values(MarkStatus).filter(
    (status) => ![MarkStatus.Assigned].concat(reviewMarkStatuses).includes(status)
  );
}

export const finishedMarkStatuses = [
  MarkStatus.FinishedSuccess,
  MarkStatus.FinishedFailure,
  MarkStatus.Replaced
];

export const finishedReviewMarkStatuses = finishedMarkStatuses.concat([
  MarkStatus.ReturnedForMarking,
  MarkStatus.Reassigned
]);

export function unfinishedMarkStatuses(): MarkStatus[] {
  return Object.values(MarkStatus).filter(
    (status) => ![MarkStatus.Assigned].concat(finishedMarkStatuses).includes(status)
  );
}

export function isMarkFinished(markStatus: MarkStatus): boolean {
  return finishedMarkStatuses.includes(markStatus);
}

/**
 * These mark statuses are set from actions being performed outside of the marking system and are
 * not configurable by Marking Admins.
 */
export const controlledMarkStatuses = [MarkStatus.ResponseReopened, MarkStatus.Replaced];

export function markStatusMessage(markStatus: MarkStatus, markType: MarkType): string {
  const howToResumeEditingMessage = `Marking admins can make changes. Editing of this mark by the active marker can resume when a marking admin sets the status to "${friendlyMarkStatus(MarkStatus.ReturnedForMarking)}" or reassigns it.`;
  const canBeModifiedMessage = (action: 'created' | 'edited'): string =>
    `Mark can be ${action} by the active marker or an admin/teacher.`;
  const canBeEditedByChangingStatusMessage = `Mark can be edited by a marking admin changing the status back to "${friendlyMarkStatus(MarkStatus.InProgress)}".`;

  switch (markStatus) {
    case MarkStatus.Assigned:
      return `The response has been assigned to a marker, but a mark has not been created. ${canBeModifiedMessage('created')}`;
    case MarkStatus.InProgress:
      return `Marking has started and this mark is in a draft state. ${canBeModifiedMessage('edited')}`;
    case MarkStatus.NeedsReview:
      return `It has been requested that this mark is looked at by a ${markType === MarkType.Autoscored ? 'teacher.' : `marking admin. ${howToResumeEditingMessage}`}`;
    case MarkStatus.InReview:
      return `This mark is being looked at by a marking admin. ${howToResumeEditingMessage}`;
    case MarkStatus.FurtherEscalated:
      return `This mark is no longer being looked at by a marking admin and has been escalated. ${howToResumeEditingMessage}`;
    case MarkStatus.ResponseReopened:
      return `This response has been reopened. Once the student has finished their response, it will become available for marking.`;
    case MarkStatus.ReturnedForMarking:
      return `This mark has been reviewed and returned to the active marker for further marking. ${canBeModifiedMessage('edited')}`;
    case MarkStatus.Reassigned:
      return `This mark has been reassigned to a new active marker. ${canBeModifiedMessage('edited')}`;
    case MarkStatus.FinishedSuccess:
      return `This mark has been submitted, so it can no longer be edited. ${canBeEditedByChangingStatusMessage}`;
    case MarkStatus.FinishedFailure:
      return `This response could not be marked successfully. ${canBeEditedByChangingStatusMessage}`;
    case MarkStatus.Replaced:
      return `This mark has been replaced by another mark, so it can no longer be edited. Edit the replacement mark instead.`;
    default:
      return systemError('Mark status is not defined in the system.');
  }
}
